import { createGrantTotalPercent, createServiceChargePercent } from "./helpers";
import { translations } from "./lang";
import { normalizeDeliveryAppKey } from "./integrations";

const COLORS = [
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
  "#24ebac",
  "#ebac24",
  "#24c6eb",
  "#ac24eb",
  "#eb2463",
  "#c6eb24",
];
const getColor = (i) => COLORS[i % COLORS.length];
const maps = (lang) => ({
  app: translations[lang].filter.apps,
});

// 🛠️ Generic Aggregator
function aggregate(data, groupKeys, fields) {
  const result = {};
  data.forEach((item) => {
    const key = groupKeys.map((k) => item[k] ?? "Unknown").join("|");
    if (!result[key]) {
      result[key] = {};
      fields.forEach((f) => {
        result[key][f] = 0;
      });
    }
    fields.forEach((f) => {
      result[key][f] += item[f] || 0;
    });
  });
  return result;
}
function monthYearToDate(monthYear) {
  const [monthStr, year] = monthYear.split("-");
  const monthIndex = new Date(`${monthStr} 1, ${year}`).getMonth();
  return new Date(year, monthIndex);
}
// 📅 Month-Year parsing
function monthYearToShort(monthYear) {
  const [monthStr, year] = monthYear.split("-");
  const monthIndex = new Date(`${monthStr} 1, ${year}`).getMonth();
  return new Date(year, monthIndex).toLocaleString("en-US", { month: "short" });
}

// 🥧 Pie Chart Data (two pies: Service Charge & GrandTotal vs Completed)
export function preparePieChartAnalysis(data, lang) {
  if (!data?.length) return { serviceChargePie: [], efficiencyPie: [] };

  const percentages = prepareAppPercentageAnalysis(data, lang);

  return {
    serviceChargePie: percentages.map(({ label, serviceValue, color }, id) => ({
      id,
      label,
      value: serviceValue,
      color,
    })),
    efficiencyPie: percentages.map(({ label, efficiencyValue, color }, id) => ({
      id,
      label,
      value: efficiencyValue,
      color,
    })),
  };
}

// Shared source for app-level percentage visualizations. Keeping both formulas
// here guarantees bar and pie consumers always calculate the same values.
export function prepareAppPercentageAnalysis(data, lang) {
  if (!data?.length) return [];

  const grouped = aggregate(
    data,
    ["app"],
    [
      "serviceCharge",
      "completedAmount",
      "grandTotal",
      "paidCampains",
      "offers",
      "deliveryOffer",
    ],
  );

  return Object.entries(grouped).map(
    (
      [
        app,
        {
          serviceCharge,
          completedAmount,
          grandTotal,
          paidCampains,
          offers,
          deliveryOffer,
        },
      ],
      i,
    ) => {
      // 🔹 Service charge % (same as before)
      const serviceValue = createGrantTotalPercent({
        completedAmount: completedAmount,
        grandTotal: grandTotal - paidCampains,
        offers: offers,
      });

      // 🔹 Efficiency % (completed vs grandTotal)
      const keetaDeliveryOffer =
        normalizeDeliveryAppKey(app) === "keeta" ? parseFloat(deliveryOffer || 0) : 0;
      const efficiencyValue = createServiceChargePercent({
        completedAmount: completedAmount,
        serviceCharge: serviceCharge,
        offers: offers,
        deliveryOffer: keetaDeliveryOffer,
      });

      const numericServiceValue = Number(serviceValue);
      const numericEfficiencyValue = Number(efficiencyValue);

      return {
        app,
        label:
          maps(lang)["app"]?.[app] ??
          translations[lang].notApp + " " + app[app.length - 1],
        serviceValue: Number.isFinite(numericServiceValue) ? numericServiceValue : 0,
        efficiencyValue: Number.isFinite(numericEfficiencyValue) ? numericEfficiencyValue : 0,
        color: getColor(i),
      };
    },
  );
}

// 🛠️ Build grouped series
function buildGroupTimeline(data, groupBy, metric, lang = "ar") {
  if (!data?.length) return { months: [], series: [] };

  const grouped = {};
  const monthsSet = new Set();

  data.forEach((item) => {
    const g = item[groupBy] || "Unknown";
    const m = item.date || "Unknown";
    monthsSet.add(m);

    if (!grouped[g]) grouped[g] = {};
    if (!grouped[g][m]) grouped[g][m] = { completedAmount: 0, grandTotal: 0 };

    grouped[g][m].completedAmount += item.completedAmount || 0;
    grouped[g][m].grandTotal += item.grandTotal || 0;
  });

  const allMonths = Array.from(monthsSet).sort(
    (a, b) => monthYearToDate(a) - monthYearToDate(b),
  );
  const shortMonths = allMonths.map(monthYearToShort);
  let totals = Object.entries(grouped).map(([g, monthsObj]) => {
    const total = allMonths.reduce(
      (sum, m) => sum + (monthsObj[m]?.[metric] || 0),
      0,
    );
    return { name: g, total };
  });

  // Sort by total and take top 8
  totals.sort((a, b) => b.total - a.total);
  const topGroups = totals.slice(0, 8).map((t) => t.name);

  const series = topGroups.map((g, i) => {
    const color = getColor(i);
    return {
      data: allMonths.map((m) => grouped[g][m]?.[metric] || 0),
      label:
        groupBy === "app"
          ? (maps(lang)[groupBy]?.[g] ??
            translations[lang].notApp + " " + g[g.length - 1])
          : g,
      color,
      showMark: false,
    };
  });

  return { months: shortMonths, series };
}

// 🛠️ Build overall chart (just 2 lines)
function buildOverallTimeline(data, lang) {
  if (!data?.length) return { months: [], series: [] };

  const grouped = {};
  const monthsSet = new Set();

  data.forEach((item) => {
    const m = item.date || "Unknown";
    monthsSet.add(m);

    if (!grouped[m]) grouped[m] = { completedAmount: 0, grandTotal: 0 };
    grouped[m].completedAmount += item.completedAmount || 0;
    grouped[m].grandTotal += item.grandTotal || 0;
  });

  const allMonths = Array.from(monthsSet).sort(
    (a, b) => monthYearToDate(a) - monthYearToDate(b),
  );
  const shortMonths = allMonths.map(monthYearToShort);

  const completed = allMonths.map((m) => grouped[m]?.completedAmount || 0);
  const total = allMonths.map((m) => grouped[m]?.grandTotal || 0);

  return {
    months: shortMonths,
    series: [
      {
        data: completed,
        label: translations[lang].Analysis.completed,
        color: getColor(0),
        showMark: false,
      },
      {
        data: total,
        label: translations[lang].Analysis.total,
        color: getColor(1),
        showMark: false,
        strokeDasharray: "6 4",
      },
    ],
  };
}

function buildOverallTimeline2(data, lang) {
  if (!data?.length) {
    return {
      months: [],
      lineSeries: [],
      barSeries: [],
    };
  }

  const grouped = {};
  const monthsSet = new Set();

  data.forEach((item) => {
    const m = item.date || "Unknown";
    monthsSet.add(m);

    if (!grouped[m]) {
      grouped[m] = {
        completed: 0, // COUNT
        completedAmount: 0, // AMOUNT
      };
    }

    grouped[m].completed += item.completed || 0;
    grouped[m].completedAmount += item.completedAmount || 0;
  });

  const allMonths = Array.from(monthsSet).sort(
    (a, b) => monthYearToDate(a) - monthYearToDate(b),
  );

  const shortMonths = allMonths.map(monthYearToShort);

  // ✅ Correct mapping
  const completedCount = allMonths.map((m) => grouped[m]?.completed || 0);

  const completedAmount = allMonths.map(
    (m) => grouped[m]?.completedAmount || 0,
  );

  return {
    months: shortMonths,

    // LINE → count
    lineSeries: [
      {
        data: completedCount,
        label: translations[lang].dailyAnalysis.count,
        showMark: false,
        color: "#003462",
      },
    ],

    // BAR → amount
    barSeries: [
      {
        data: completedAmount,
        label: translations[lang].dailyAnalysis.amount,
        color: "#c6eb24",
      },
    ],
  };
}

// 🛠️ Build Offers Campaign Timeline
function buildOffersCampaignTimeline(data, lang) {
  if (!data?.length) return { months: [], series: [] };

  const grouped = {};
  const monthsSet = new Set();

  data.forEach((item) => {
    const m = item.date || "Unknown";
    monthsSet.add(m);

    if (!grouped[m]) grouped[m] = { deliveryOffer: 0, offers: 0, completed: 0 };
    grouped[m].deliveryOffer += item.deliveryOffer || 0;
    grouped[m].offers += item.offers || 0;
    grouped[m].completed += item.completed || 0;
  });

  const allMonths = Array.from(monthsSet).sort(
    (a, b) => monthYearToDate(a) - monthYearToDate(b),
  );
  const shortMonths = allMonths.map(monthYearToShort);

  const deliveryOfferLine = allMonths.map(
    (m) => grouped[m]?.deliveryOffer || 0,
  );
  const offersLine = allMonths.map((m) => grouped[m]?.offers || 0);
  const completedLine = allMonths.map((m) => grouped[m]?.completed || 0);

  return {
    months: shortMonths,
    series: [
      {
        data: deliveryOfferLine,
        label: translations[lang].Analysis.campain[0],
        color: getColor(0),
        showMark: false,
      },
      {
        data: offersLine,
        label: translations[lang].Analysis.campain[1],
        color: getColor(1),
        showMark: false,
      },
      {
        data: completedLine,
        label: translations[lang].Analysis.campain[2],
        color: getColor(2),
        showMark: false,
      },
    ],
  };
}

// 🛠️ Build Amounts Timeline
function buildDetailsTimeline(data, lang) {
  if (!data?.length) return { months: [], series: [] };

  const grouped = {};
  const monthsSet = new Set();

  data.forEach((item) => {
    const m = item.date || "Unknown";
    monthsSet.add(m);

    if (!grouped[m]) {
      grouped[m] = {
        rejected: 0,
        canceled: 0,
        endurance: 0,
        refund: 0,
      };
    }

    grouped[m].rejected += item.rejected || 0;
    grouped[m].canceled += item.canceled || 0;
    grouped[m].endurance += item.endurance || 0;
    grouped[m].refund += item.refund || 0;
  });

  // Sort months chronologically
  const allMonths = Array.from(monthsSet).sort(
    (a, b) => monthYearToDate(a) - monthYearToDate(b),
  );
  const shortMonths = allMonths.map(monthYearToShort);

  // Build lines
  const rejectedLine = allMonths.map((m) => grouped[m]?.rejected || 0);
  const canceledLine = allMonths.map((m) => grouped[m]?.canceled || 0);
  const enduranceLine = allMonths.map((m) => grouped[m]?.endurance || 0);
  const refundLine = allMonths.map((m) => grouped[m]?.refund || 0);

  return {
    months: shortMonths,
    series: [
      {
        data: rejectedLine,
        label: translations[lang].Analysis.statuses[0],
        color: getColor(0),
        showMark: false,
      },
      {
        data: canceledLine,
        label: translations[lang].Analysis.statuses[1],
        color: getColor(1),
        showMark: false,
      },
      {
        data: enduranceLine,
        label: translations[lang].Analysis.statuses[2],
        color: getColor(2),
        showMark: false,
      },
      {
        data: refundLine,
        label: translations[lang].Analysis.statuses[3],
        color: getColor(3),
        showMark: false,
      },
    ],
  };
}

// 📈 Generate all 7 timelines
export function prepareAllTimelines(data, lang) {
  return {
    overallold: buildOverallTimeline(data, lang), // 1 chart
    overall: buildOverallTimeline2(data, lang), // 1 chart
    byAppCompleted: buildGroupTimeline(data, "app", "completedAmount", lang), // 1 chart
    // byAppTotal: buildGroupTimeline(data, "app", "grandTotal"),           // 1 chart
    byBranchCompleted: buildGroupTimeline(data, "branchName", "completedAmount"), // 1 chart
    // byBranchTotal: buildGroupTimeline(data, "branch", "grandTotal"),         // 1 chart
    byStoreCompleted: buildGroupTimeline(data, "storeName", "completedAmount"), // 1 chart
    // byStoreTotal: buildGroupTimeline(data, "store", "grandTotal"),           // 1 chart
    offersCampaign: buildOffersCampaignTimeline(data, lang),
    detailsAmount: buildDetailsTimeline(data, lang),
  };
}

